import React, { useEffect, useState } from "react";
import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { isAuthenticated } from "../utils/auth";

import AuthCallback from "../pages/Login/AuthCallback";
import Login from "../pages/Login";
import SignUpProcess from "../pages/Login/MockupPages/SignUpProcess";
import DashboardLayout from "ui/layout";
import Dashboard from "pages/Dashboard";
import AInsight from "pages/AInsight";
import Topology from "pages/Topology";
import Devices from "pages/Devices";
import Events from "pages/Events";
import FirmwareManagements from "pages/FirmwareManagements";
import Clients from "pages/Clients";
import UserManagement from "pages/System/UserManagement";
import RealmSetting from "pages/System/RealmSetting";
import UserProfile from "pages/Profile";
import Realm from "pages/ScopeLevel/Realm";
import Organization from "pages/ScopeLevel/Organization";
import Site from "pages/ScopeLevel/Site";
import UserManagementController from "pages/System/UserManagement/Controller";
import Support from "pages/Support";
import LicenseManagement from "pages/LicenseManagement";
import WsDebugger from "pages/WsDebugger";

import localStorageUtil from "utils/localStorageUtil";
import useDataSourceManager from "hooks/useDataSourceManager";
import { v4 as uuidv4 } from "uuid";

import { Box, Typography, Divider, IconButton } from "@mui/material";
import { ReactComponent as CloseIcon } from "assets/images/icons/btn_close.svg";

import ModalContainer from "components/ModalContainer";
import { SolidButton } from "components/buttons/SolidButton";

const checkMockup = process.env.REACT_APP_IS_MOCKUP;
const checkEnvironment = process.env.REACT_APP_RUNNING_CONTEXT;

const currentSelectBreadcrumbs = localStorage.getItem("selectBreadcrumbs");
const storedSelectedRealmData = localStorageUtil.getItemField(
  "selectBreadcrumbs",
  "realm",
);
const storedSelectedOrgData = localStorageUtil.getItemField(
  "selectBreadcrumbs",
  "orgs",
);
const storedSelectedSiteData = localStorageUtil.getItemField(
  "selectBreadcrumbs",
  "site",
);

const isEmptyRealm =
  !storedSelectedRealmData || Object.keys(storedSelectedRealmData).length === 0;
const isEmptyOrg =
  !storedSelectedOrgData || Object.keys(storedSelectedOrgData).length === 0;
const isEmptySite =
  !storedSelectedSiteData || Object.keys(storedSelectedSiteData).length === 0;

const siteRule = !isEmptyRealm && !isEmptyOrg && !isEmptySite;
const standaloneSiteRule =
  storedSelectedRealmData &&
  storedSelectedRealmData.hasManageOrg === false &&
  !isEmptyRealm &&
  isEmptyOrg &&
  !isEmptySite;
const orgRule = !isEmptyRealm && !isEmptyOrg && isEmptySite;
const standaloneOrgRule =
  storedSelectedRealmData &&
  storedSelectedRealmData.hasManageOrg === false &&
  !isEmptyRealm &&
  isEmptyOrg &&
  isEmptySite;
const mspRule = !isEmptyRealm && isEmptyOrg && isEmptySite;
const realmRule =
  currentSelectBreadcrumbs === null ||
  (isEmptyRealm && isEmptyOrg && isEmptySite);

const WhatsNewModal = () => {
  const [newsUuid] = useState(uuidv4());
  const [announcementUuid] = useState(uuidv4());
  const { dataMap, sendRequest } = useDataSourceManager();

  const [openWhatsNewModal, setOpenWhatsNewModal] = useState(false);
  const [modalSteps, setModalSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const getReadIds = (storageKey) => {
    try {
      const value = localStorage.getItem(storageKey);
      if (!value) return [];

      const parsedValue = JSON.parse(value);

      return Array.isArray(parsedValue)
        ? parsedValue.map((id) => String(id))
        : [];
    } catch (error) {
      return [];
    }
  };

  const saveReadIds = (storageKey, ids) => {
    const readIds = getReadIds(storageKey);
    const mergedIds = [
      ...new Set([...readIds, ...ids.map((id) => String(id))]),
    ];

    localStorage.setItem(storageKey, JSON.stringify(mergedIds));
  };
  //轉換格式
  const formatDate = (date) => {
    if (!date) return "";

    const dateValue =
      typeof date === "number" || /^\d+$/.test(String(date))
        ? Number(date) * 1000
        : date;

    return new Date(dateValue).toISOString().split("T")[0];
  };
  useEffect(() => {
    sendRequest("/system/news.read", newsUuid);
    sendRequest("/system/announcement.read", announcementUuid);
  }, [sendRequest, newsUuid, announcementUuid]);

  useEffect(() => {
    const newsResponse = dataMap[newsUuid];
    const announcementResponse = dataMap[announcementUuid];

    if (!newsResponse || !announcementResponse) return;

    const newsData = newsResponse?.result?.data || newsResponse?.data || [];
    const announcementData =
      announcementResponse?.result?.data || announcementResponse?.data || [];

    const readNewsIds = getReadIds("newsIds");
    const readAnnouncementIds = getReadIds("announcementIds");

    const unreadNews = Array.isArray(newsData)
      ? newsData.filter((news) => !readNewsIds.includes(String(news.id)))
      : [];

    const unreadAnnouncements = Array.isArray(announcementData)
      ? announcementData.filter(
          (announcement) =>
            !readAnnouncementIds.includes(String(announcement.id)),
        )
      : [];

    const steps = [];

    if (unreadNews.length > 0) {
      steps.push({
        type: "news",
        title: "What's New",
        storageKey: "newsIds",
        items: unreadNews,
      });
    }

    if (unreadAnnouncements.length > 0) {
      steps.push({
        type: "announcement",
        title: "Announcement",
        storageKey: "announcementIds",
        items: unreadAnnouncements,
      });
    }

    setModalSteps(steps);
    setCurrentStepIndex(0);
    setOpenWhatsNewModal(steps.length > 0);
  }, [dataMap, newsUuid, announcementUuid]);

  const markCurrentStepAsRead = () => {
    const currentStep = modalSteps[currentStepIndex];

    if (!currentStep) return;

    const currentIds = currentStep.items.map((item) => String(item.id));

    saveReadIds(currentStep.storageKey, currentIds);
  };

  const handleNext = () => {
    markCurrentStepAsRead();

    if (currentStepIndex < modalSteps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
      return;
    }

    setOpenWhatsNewModal(false);
  };

  const handleCloseWhatsNewModal = () => {
    modalSteps.forEach((step) => {
      const ids = step.items.map((item) => String(item.id));
      saveReadIds(step.storageKey, ids);
    });

    setOpenWhatsNewModal(false);
  };

  const currentStep = modalSteps[currentStepIndex];
  const isLastStep = currentStepIndex === modalSteps.length - 1;

  return (
    <ModalContainer
      display={openWhatsNewModal}
      modalWidth="600px"
      children={
        <Box
          sx={{
            width: "100%",
            bgcolor: "#FFFFFF",
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              pt: 2,
              pb: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 600,
                color: "rgba(0, 0, 0, 0.8)",
              }}
            >
              {currentStep?.title}
            </Typography>

            <IconButton size="small" onClick={handleCloseWhatsNewModal}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              px: 2,
              pb: 2,
              maxHeight: "380px",
              overflowY: "auto",
            }}
          >
            {currentStep?.items?.map((item, index) => (
              <Box key={item.id}>
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "#172664",
                    mb: 1,
                  }}
                >
                  {item.title}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#8A8A8A",
                    mb: 2,
                  }}
                >
                  {formatDate(item.updatedAt || item.createdAt)}
                </Typography>

                <Box
                  sx={{
                    fontSize: "12px",
                    color: "rgba(0, 0, 0, 0.8)",
                    lineHeight: 1.8,
                    mb: 3,

                    "& p": {
                      margin: "0 0 12px 0",
                    },

                    "& h2": {
                      fontSize: "18px",
                      margin: "0 0 12px 0",
                    },

                    "& h3": {
                      fontSize: "16px",
                      margin: "16px 0 8px 0",
                    },

                    "& ol, & ul": {
                      margin: "0 0 16px 0",
                      paddingLeft: "24px",
                    },

                    "& li": {
                      marginBottom: "8px",
                    },

                    "& strong": {
                      fontWeight: 600,
                    },

                    "& em": {
                      fontStyle: "italic",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: item.content || "",
                  }}
                />

                {index !== currentStep.items.length - 1 && (
                  <Divider sx={{ my: 3 }} />
                )}
              </Box>
            ))}
          </Box>

          <Divider />

          <Box
            sx={{
              px: 2,
              py: 2,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <SolidButton width="72px" height="32px" onClick={handleNext}>
              {isLastStep ? "Close" : "Next"}
            </SolidButton>
          </Box>
        </Box>
      }
    />
  );
};

const NewsModal = ({ element }) => {
  return (
    <>
      <WhatsNewModal />
      {element}
    </>
  );
};

const ProtectedRoute = ({ element }) => {
  const location = useLocation();
  const level = location.pathname.split("/")[1];
  const { authProcess, setAuthProcess } = useAuth();

  if (checkMockup === "true") {
    return <NewsModal element={element} />;
  }

  if (isAuthenticated()) {
    if (!authProcess) {
      setAuthProcess("auth");

      if (siteRule) {
        if (level === "org") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});
        }

        if (level === "msp") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});
          localStorageUtil.updateItemField("selectBreadcrumbs", "orgs", {});
        }

        if (level === "realms") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});
          localStorageUtil.updateItemField("selectBreadcrumbs", "orgs", {});
          localStorageUtil.updateItemField("selectBreadcrumbs", "realm", {});
        }

        return <NewsModal element={element} />;
      }

      if (standaloneSiteRule) {
        if (level === "org") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});
        }

        if (level === "msp") {
          return <Navigate to="/" />;
        }

        if (level === "realms") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});
          localStorageUtil.updateItemField("selectBreadcrumbs", "realm", {});
        }

        return <NewsModal element={element} />;
      }

      if (orgRule) {
        if (level === "site") {
          return <Navigate to="/" />;
        }

        if (level === "msp") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});
          localStorageUtil.updateItemField("selectBreadcrumbs", "orgs", {});
        }

        if (level === "realms") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});
          localStorageUtil.updateItemField("selectBreadcrumbs", "orgs", {});
          localStorageUtil.updateItemField("selectBreadcrumbs", "realm", {});
        }

        return <NewsModal element={element} />;
      }

      if (standaloneOrgRule) {
        if (level === "site" || level === "msp") {
          return <Navigate to="/" />;
        }

        if (level === "realms") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});
          localStorageUtil.updateItemField("selectBreadcrumbs", "realm", {});
        }

        return <NewsModal element={element} />;
      }

      if (mspRule) {
        if (level === "site" || level === "org") {
          return <Navigate to="/" />;
        }

        if (level === "realms") {
          localStorageUtil.updateItemField("selectBreadcrumbs", "site", {});
          localStorageUtil.updateItemField("selectBreadcrumbs", "orgs", {});
          localStorageUtil.updateItemField("selectBreadcrumbs", "realm", {});
        }

        return <NewsModal element={element} />;
      }

      if (realmRule) {
        if (level === "site" || level === "org" || level === "msp") {
          return <Navigate to="/" />;
        }

        return <NewsModal element={element} />;
      }
    }

    return <NewsModal element={element} />;
  }

  if (location.pathname) {
    const fullPath = location.pathname + location.search;
    localStorage.setItem("redirectPath", fullPath);

    const params = new URLSearchParams(location.search || "");
    const vId = params.get("v_id");
    const isInvitationPath = location.pathname.includes("/invitation");

    if (vId && isInvitationPath) {
      sessionStorage.setItem("pendingInvitationId", vId);
    } else {
      sessionStorage.removeItem("pendingInvitationId");
    }

    return <Navigate to="/login" replace />;
  }

  sessionStorage.removeItem("pendingInvitationId");
  return <Navigate to="/login" replace />;
};

const routes = [
  {
    path: "/",
    element: (() => {
      if (!isAuthenticated()) {
        return <Login />;
      }

      if (checkEnvironment === "cloud") {
        if (realmRule) {
          return <Navigate to="/realms" />;
        }

        if (mspRule) {
          if (storedSelectedRealmData.hasManageOrg === true) {
            return <Navigate to="/msp/organizations" />;
          }

          return <Navigate to="/org/sites" />;
        }

        if (orgRule) {
          return <Navigate to="/org/sites" />;
        }

        return <Navigate to="/site/dashboard" />;
      }

      if (checkEnvironment === "premises") {
        if (!isEmptyRealm && !isEmptySite) {
          return <Navigate to="/site/dashboard" />;
        }

        return <Navigate to="/org/sites" />;
      }

      return <Login />;
    })(),
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/callback",
    element: <AuthCallback />,
  },
  {
    path: "/signup",
    element:
      checkMockup === "true" && checkEnvironment === "cloud" ? (
        <SignUpProcess />
      ) : (
        <Navigate to="*" />
      ),
  },
  {
    path: "/ws-debugger",
    element: <WsDebugger />,
  },
  {
    path: "/",
    element: <DashboardLayout />,
    children: [
      {
        path: "/site/dashboard",
        element: <ProtectedRoute element={<Dashboard />} />,
      },
      {
        path: "/site/AInsight",
        element: <ProtectedRoute element={<AInsight />} />,
      },
      {
        path: "site/topology",
        element: <ProtectedRoute element={<Topology />} />,
      },
      {
        path: "clients",
        element: <ProtectedRoute element={<Clients />} />,
      },
      {
        path: "/account/profile",
        element: <ProtectedRoute element={<UserProfile />} />,
      },
      {
        path: "/realms",
        element: <ProtectedRoute element={<Realm />} />,
      },
      {
        path: "/msp/organizations",
        element: <ProtectedRoute element={<Organization />} />,
      },
      {
        path: "/org/sites",
        element: <ProtectedRoute element={<Site />} />,
      },
      {
        path: "invitation",
        element: <ProtectedRoute element={<UserManagementController />} />,
      },
      {
        path: "msp/users",
        element: <ProtectedRoute element={<UserManagement />} />,
      },
      {
        path: "org/users",
        element: <ProtectedRoute element={<UserManagement />} />,
      },
      {
        path: "/org/devices",
        element: <ProtectedRoute element={<Devices />} />,
      },
      {
        path: "/site/devices",
        element: <ProtectedRoute element={<Devices />} />,
      },
      {
        path: "/org/events",
        element: <ProtectedRoute element={<Events />} />,
      },
      {
        path: "/site/events",
        element: <ProtectedRoute element={<Events />} />,
      },
      {
        path: "/org/firmware_managements",
        element: <ProtectedRoute element={<FirmwareManagements />} />,
      },
      {
        path: "/site/firmware_managements",
        element: <ProtectedRoute element={<FirmwareManagements />} />,
      },
      {
        path: "/msp/settings",
        element: <ProtectedRoute element={<RealmSetting />} />,
      },
      {
        path: "/org/settings",
        element: <ProtectedRoute element={<RealmSetting />} />,
      },
      {
        path: "/site/settings",
        element: <ProtectedRoute element={<RealmSetting />} />,
      },
      {
        path: "/msp/support",
        element: <ProtectedRoute element={<Support />} />,
      },
      {
        path: "/org/support",
        element: <ProtectedRoute element={<Support />} />,
      },
      {
        path: "/site/support",
        element: <ProtectedRoute element={<Support />} />,
      },
      {
        path: "/license",
        element: <ProtectedRoute element={<LicenseManagement />} />,
      },
      {
        path: "*",
        element: <Navigate to="/" />,
      },
    ],
  },
];

const router = createBrowserRouter(routes);

export default router;
